// CompletedTicket.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { formatDate } from "../utils/general_util";
import TicketDetailsDialog from "../components/TicketDetailsDialog";
import { roundTo } from "../utils/general_util";
import { toast } from "react-toastify";

function prettyTicketType(type) {
    if (!type) return "N/A";
    return type === "reactive" ? "Reactive" : "Proactive";
}

function prettyStatus(status) {
    if (!status) return "Completed";
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1);
}

const CompletedTicket = () => {
    const [role, setRole] = useState("user");
    const isAdmin = role === "admin";

    const [ready, setReady] = useState(false);

    const [email, setEmail] = useState("");
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("bcr_desc");

    // Dialog state
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [doneModalOpen, setDoneModalOpen] = useState(false);
    const [doneTicketId, setDoneTicketId] = useState(null);
    const [finalCommentsDraft, setFinalCommentsDraft] = useState("");
    const [markingDone, setMarkingDone] = useState(false);

    // Admin: created_by dropdown
    const [createdByFilter, setCreatedByFilter] = useState("all"); // "all" or email
    const [createdByOptions, setCreatedByOptions] = useState([]); // dropdown options

    const [implementationFilter, setImplementationFilter] = useState("all"); // all | doing | done

    useEffect(() => {
        if (typeof window === "undefined") return;

        const storedRole = window.localStorage.getItem("role") || "user";
        setRole(storedRole);

        const storedEmail =
            window.localStorage.getItem("userEmail") ||
            window.localStorage.getItem("email") ||
            "";

        // Only require email for non-admin
        if (!storedEmail && storedRole !== "admin") {
            setError(
                "No user email found in localStorage. Completed tickets cannot be loaded."
            );
            setLoading(false);
            setReady(true);
            return;
        }

        setEmail(storedEmail);
        setReady(true);
    }, []);

    useEffect(() => {
        if (!ready) return;
        if (!isAdmin && !email) return;

        const fetchTickets = async () => {
            setLoading(true);
            setError("");

            try {
                const params = { status: "completed" };

                if (isAdmin) {
                    // Admin can filter by creator only (optional)
                    if (createdByFilter !== "all") {
                        params.created_by = createdByFilter;
                    }
                } else {
                    // Non-admin: created_by OR reviewed_by (backend ORs when both exist)
                    params.created_by = email;
                    params.reviewed_by = email;
                }

                const res = await axiosInstance.get("/ticket/", { params });
                const data = res.data || [];
                setTickets(data);

                // Populate dropdown options when admin is viewing "All users"
                if (isAdmin && createdByFilter === "all") {
                    const uniqueCreators = Array.from(
                        new Set(
                            data
                                .map((t) => t.created_by || t.createdBy)
                                .filter(Boolean)
                        )
                    ).sort((a, b) => String(a).localeCompare(String(b)));

                    setCreatedByOptions(uniqueCreators);
                }
            } catch (err) {
                console.error("Failed to load completed tickets:", err);
                setError("Failed to load completed tickets. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchTickets();
    }, [ready, isAdmin, email, createdByFilter]);

    const filteredAndSorted = useMemo(() => {
        let list = Array.isArray(tickets) ? [...tickets] : [];

        const term = search.trim().toLowerCase();
        if (term) {
            const isNumeric = /^\d+$/.test(term);
            list = list.filter((t) => {
                const title = (t.title || t.ticketTitle || "").toLowerCase();
                const idStr = String(t.ticketId || "");
                if (isNumeric) return idStr.includes(term) || title.includes(term);
                return title.includes(term);
            });
        }

        // Implementation status filter
        if (implementationFilter !== "all") {
            list = list.filter((t) => {
                const s = String(t.implementationStatus || "").trim().toLowerCase();
                return s === implementationFilter; // matches "doing" or "done"
            });
        }

        list.sort((a, b) => {
            if (sortBy.startsWith("bcr")) {
                const aBcr = Number(a.benefitCostRatio);
                const bBcr = Number(b.benefitCostRatio);

                const aValid = Number.isFinite(aBcr);
                const bValid = Number.isFinite(bBcr);

                // Push missing/invalid BCR to the bottom
                if (!aValid && !bValid) return 0;
                if (!aValid) return 1;
                if (!bValid) return -1;

                return sortBy === "bcr_asc" ? aBcr - bBcr : bBcr - aBcr;
            }

            const da = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const db = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return sortBy === "date_asc" ? da - db : db - da;
        });

        return list;
    }, [tickets, search, sortBy, implementationFilter]);

    function handleOpenDetails(ticket) {
        setSelectedTicket(ticket);
    }

    function handleCloseDetails() {
        setSelectedTicket(null);
    }

    const handleMarkAsDone = async (ticketId) => {
        setDoneTicketId(ticketId);
        setFinalCommentsDraft("");
        setDoneModalOpen(true);
    };

    const submitMarkAsDone = async () => {
        if (!doneTicketId) return;
        try {
            setMarkingDone(true);
            await axiosInstance.post(`/ticket/${doneTicketId}/done`, {
                finalComments: finalCommentsDraft,
            });
            // Update ticket in local state
            setTickets((prevTickets) =>
                prevTickets.map((t) =>
                    t.ticketId === doneTicketId
                        ? { ...t, implementationStatus: "Done" }
                        : t
                )
            );
            toast.success("Ticket marked as done successfully.");
            setDoneModalOpen(false);
            setDoneTicketId(null);
            setFinalCommentsDraft("");
            handleCloseDetails();
        } catch (err) {
            toast.error("Failed to mark ticket as done. Please try again.");
        } finally {
            setMarkingDone(false);
        }
    };

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl rounded-3xl bg-white/95 p-8 shadow-2xl ring-1 ring-slate-200">
                <header className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900 text-left">
                            Completed Tickets
                        </h1>
                        <p className="mt-1 text-sm text-slate-600 text-left">
                            Approved tickets ready for the creator to implement.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs sm:text-xs">
                        {(email || isAdmin) && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                                Logged as: {email || "Admin"}
                            </span>
                        )}
                    </div>
                </header>

                {error && (
                    <div className="mb-4 rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {error}
                    </div>
                )}

                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">
                            Search
                        </label>
                        <input
                            type="text"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            placeholder="e.g. 102 or 'Production efficiency'"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="w-full sm:w-56">
                        <div className="w-full sm:w-56">
                            <label className="mb-1 block text-xs font-semibold text-slate-700">
                                Sort By
                            </label>
                            <select
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="bcr_desc">BCR: Highest first</option>
                                <option value="bcr_asc">BCR: Lowest first</option>
                                <option value="date_desc">Date: Newest first</option>
                                <option value="date_asc">Date: Oldest first</option>
                            </select>
                        </div>
                    </div>

                    <div className="w-full sm:w-56">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">
                            Implementation
                        </label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={implementationFilter}
                            onChange={(e) => setImplementationFilter(e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="doing">Doing</option>
                            <option value="done">Done</option>
                        </select>
                    </div>

                    {isAdmin && (
                        <div className="w-full sm:w-72">
                            <label className="mb-1 block text-xs font-semibold text-slate-700">
                                Created by
                            </label>
                            <select
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={createdByFilter}
                                onChange={(e) => setCreatedByFilter(e.target.value)}
                            >
                                <option value="all">All users</option>

                                {createdByOptions.length === 0 ? (
                                    <option value="" disabled>
                                        No creators found
                                    </option>
                                ) : (
                                    createdByOptions.map((creatorEmail) => (
                                        <option key={creatorEmail} value={creatorEmail}>
                                            {creatorEmail}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        Loading completed tickets...
                    </div>
                )}

                {!loading && !error && filteredAndSorted.length === 0 && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        No completed tickets found{isAdmin ? "." : " for your account."}
                    </div>
                )}

                {!loading && !error && filteredAndSorted.length > 0 && (
                    <div className="mt-4 space-y-3">
                        {filteredAndSorted.map((t) => (
                            <article
                                key={t.ticketId}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpenDetails(t)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenDetails(t);
                                    }
                                }}
                                className="cursor-pointer rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="flex-1 min-w-0 space-y-1.5 text-left">
                                        <div className="flex items-center gap-2">
                                            <p className="text-xs sm:text-sm text-slate-500">
                                                Ticket ID:{" "}
                                                <span className="font-semibold text-slate-800">
                                                    {t.ticketId ?? "N/A"}
                                                </span>
                                            </p>

                                            {t.implementationStatus && (() => {
                                                const status = t.implementationStatus.trim().toLowerCase();

                                                const classesByStatus = {
                                                    doing: "bg-yellow-500 text-white",
                                                    done: "bg-green-500 text-white",
                                                };

                                                const colourClasses = classesByStatus[status] ?? "bg-red-500 text-white";

                                                return (
                                                    <span
                                                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium shadow-sm ${colourClasses}`}
                                                    >
                                                        {t.implementationStatus}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <h2 className="text-sm sm:text-base font-semibold text-slate-900 line-clamp-2">
                                            {t.title || t.ticketTitle || "(No title)"}
                                        </h2>

                                        {t.description && (
                                            <p className="mt-1.5 text-xs sm:text-sm text-slate-600 line-clamp-3 break-words">
                                                {t.description}
                                            </p>
                                        )}

                                        <div className="text-xs text-slate-600">
                                            Created: {formatDate(t.createdAt)}
                                        </div>

                                        {t.deadline && (
                                            <div className="text-xs text-red-600 font-bold">
                                                Deadline: {formatDate(t.deadline)}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-3 flex flex-row items-center justify-between gap-3 text-xs sm:mt-0 sm:flex-col sm:items-end sm:justify-start sm:text-xs">
                                        <div className="flex flex-row flex-wrap gap-2 sm:flex-col sm:items-end">
                                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100 shadow-sm">
                                                {prettyStatus(t.status) || "Completed"}
                                            </span>

                                            {t.ticketType && (
                                                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 shadow-sm">
                                                    {prettyTicketType(t.ticketType)}
                                                </span>
                                            )}

                                            {t.benefitCostRatio && (
                                                <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-200 shadow-sm">
                                                    BCR: {t.benefitCostRatio ? roundTo(Number(t.benefitCostRatio), 1) : "N/A"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                {selectedTicket && (
                    <TicketDetailsDialog
                        ticket={selectedTicket}
                        onClose={handleCloseDetails}
                        isFinished={true}
                        onMarkAsDone={handleMarkAsDone}
                        inlineNotesEditable={true}
                        currentUserEmail={email}
                    />
                )}

                {doneModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                        <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
                            <h3 className="text-base font-semibold text-slate-900 text-left">
                                Final Comments (Optional)
                            </h3>
                            <p className="mt-2 text-sm text-slate-600 text-left">
                                Add any final comments, conclusions, and/or what was ultimately implemented.
                            </p>
                            <textarea
                                className="mt-3 min-h-[160px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                placeholder="Optional final comments..."
                                value={finalCommentsDraft}
                                onChange={(e) => setFinalCommentsDraft(e.target.value)}
                                disabled={markingDone}
                            />
                            <div className="mt-5 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (markingDone) return;
                                        setDoneModalOpen(false);
                                        setDoneTicketId(null);
                                        setFinalCommentsDraft("");
                                    }}
                                    className="inline-flex items-center rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 shadow-sm transition hover:bg-slate-200"
                                    disabled={markingDone}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={submitMarkAsDone}
                                    className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={markingDone}
                                >
                                    {markingDone ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompletedTicket;
